import React, { useState } from "react";
import {
  Table,
  Input,
  Button,
  Badge,
  UncontrolledTooltip,
  Modal,
  ModalBody,
  ModalHeader,
} from "reactstrap";
import { updateImpactedPart, updateImpactedPartStatus } from "./service";

const ImpactedPartsTable = ({ parts, constants, onUpdate, onDelete, readOnly = false }) => {
  const [editingPart, setEditingPart] = useState(null);
  const [editData, setEditData] = useState({});
  const [imageModal, setImageModal] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);

  const handleEditStart = (part) => {
    if (!part || !part.id) {
      console.error("Invalid part data:", part);
      return;
    }
    setEditingPart(part.id);
    setEditData({
      issue_description: (part.issue_description && typeof part.issue_description === 'string') ? part.issue_description : "",
      categories: (Array.isArray(part.categories)) ? part.categories : [],
      resolution: (part.resolution && typeof part.resolution === 'string') ? part.resolution : "",
      analysis: (part.analysis && typeof part.analysis === 'string') ? part.analysis : "",
    });
  };

  const handleEditCancel = () => {
    setEditingPart(null);
    setEditData({});
  };

  const handleEditSave = async (partId) => {
    try {
      const formData = new FormData();
      Object.keys(editData).forEach((key) => {
        if (key === "categories") {
          formData.append(key, JSON.stringify(editData[key]));
        } else if (editData[key] !== undefined && editData[key] !== null) {
          formData.append(key, editData[key]);
        }
      });

      await updateImpactedPart(partId, formData);
      setEditingPart(null);
      setEditData({});
      if (onUpdate) onUpdate();
    } catch (err) {
      // API error format: { errors: { field: ["message"] } } with status :unprocessable_entity
      // Success format: { impacted_part: {...} }
      let errorMessage = "Failed to update part";
      if (err) {
        if (err.response && err.response.data && err.response.data.errors) {
          // Format: { errors: { field: ["message"] } }
          const errors = err.response.data.errors;
          const firstErrorField = Object.keys(errors)[0];
          if (firstErrorField && Array.isArray(errors[firstErrorField]) && errors[firstErrorField].length > 0) {
            errorMessage = errors[firstErrorField][0];
          } else if (firstErrorField) {
            errorMessage = String(errors[firstErrorField]);
          }
        } else if (err.error) {
          if (typeof err.error === 'string') {
            errorMessage = err.error;
          } else if (err.error?.message) {
            errorMessage = err.error.message;
          }
        } else if (err.message) {
          errorMessage = err.message;
        }
      }
      alert("Failed to update part: " + errorMessage);
      console.error("Update part error:", err);
    }
  };

  const handleStatusChangeWithConfirmation = (partId, newStatus, currentStatus) => {
    const statusLabels = {
      open: "Open",
      analysed: "Analysed",
      received: "Received",
      production: "Production",
      delivered: "Delivered",
    };

    const currentLabel = statusLabels[currentStatus] || currentStatus;
    const newLabel = statusLabels[newStatus] || newStatus;

    if (window.confirm(`Are you sure you want to change status from "${currentLabel}" to "${newLabel}"?`)) {
      handleStatusChange(partId, newStatus, currentStatus);
    }
  };

  const handleStatusChange = async (partId, newStatus, currentStatus) => {
    console.log("handleStatusChange called:", { partId, newStatus, currentStatus });

    // If status hasn't changed, don't do anything
    if (newStatus === currentStatus) {
      console.log("Status unchanged, skipping");
      return;
    }

    console.log("Step 1: Checking constants...", {
      hasConstants: !!constants,
      hasValidTransitions: !!constants?.valid_transitions,
      hasPartTransitions: !!constants?.valid_transitions?.part
    });

    // Show warning if transition seems invalid, but don't block - let backend validate
    // Backend is the source of truth for valid transitions
    if (constants && constants.valid_transitions && constants.valid_transitions.part && constants.valid_transitions.part[currentStatus]) {
      const allowedTransitions = constants.valid_transitions.part[currentStatus];
      if (Array.isArray(allowedTransitions)) {
        console.log("Step 2: Checking transitions (informational only)...", {
          currentStatus,
          newStatus,
          allowedTransitions,
          isAllowed: allowedTransitions.includes(newStatus)
        });
        if (!allowedTransitions.includes(newStatus)) {
          console.warn("Transition may be invalid according to frontend constants, but proceeding - backend will validate");
          // Don't block - just warn. Backend will reject if truly invalid.
        }
      }
    } else {
      console.log("Constants not fully loaded - letting backend validate");
    }

    console.log("Step 3: Finding part...");
    // Get the part to check analysis requirement
    if (!Array.isArray(parts)) {
      console.error("Parts is not an array:", parts);
      alert("Invalid parts data");
      return;
    }
    const part = parts.find((p) => p && p.id === partId);
    if (!part) {
      console.error("Part not found:", partId);
      alert("Part not found");
      return;
    }
    console.log("Step 4: Part found", part);

    // Check if analysis is required
    let analysisToSend = null;
    if (newStatus === "analysed") {
      console.log("Step 5: Checking analysis requirement...");
      const partAnalysis = part.analysis;
      if (!partAnalysis || (typeof partAnalysis === 'string' && partAnalysis.trim() === '')) {
        alert("Analysis is required to move to 'analysed' status. Please edit the part and add analysis first.");
        return;
      }
      analysisToSend = (typeof partAnalysis === 'string') ? partAnalysis : String(partAnalysis);
    }
    console.log("Step 6: All validations passed, proceeding to API call...");

    try {
      setUpdatingStatus(partId);
      console.log("Calling updateImpactedPartStatus API...", {
        partId,
        newStatus,
        analysis: analysisToSend,
        endpoint: `/impacted_parts/${partId}/update_status`
      });

      const response = await updateImpactedPartStatus(partId, newStatus, analysisToSend);
      console.log("Status update successful:", response);

      // API returns: { impacted_part: {...}, ticket: {...} }
      if (response && response.impacted_part) {
        console.log("Update confirmed, refreshing ticket data...");
        if (onUpdate) {
          onUpdate();
        } else {
          console.warn("onUpdate callback not provided!");
        }
      } else {
        console.warn("Unexpected response format:", response);
        // Still refresh even if response format is unexpected
        if (onUpdate) onUpdate();
      }
    } catch (err) {
      console.error("Status update failed - Full error:", err);
      console.error("Error details:", {
        message: err.message,
        error: err.error,
        response: err.response,
        status: err.response?.status,
        data: err.response?.data
      });

      // API error format: { errors: { field: ["message"] } } with status :unprocessable_entity
      let errorMessage = "Failed to update status";
      if (err) {
        if (err.response && err.response.data && err.response.data.errors) {
          // Format: { errors: { field: ["message"] } }
          const errors = err.response.data.errors;
          const firstErrorField = Object.keys(errors)[0];
          if (firstErrorField && Array.isArray(errors[firstErrorField]) && errors[firstErrorField].length > 0) {
            errorMessage = errors[firstErrorField][0];
          } else if (firstErrorField) {
            errorMessage = String(errors[firstErrorField]);
          }
        } else if (err.error) {
          if (typeof err.error === 'string') {
            errorMessage = err.error;
          } else if (err.error?.message) {
            errorMessage = err.error.message;
          }
        } else if (err.message) {
          errorMessage = err.message;
        }
      }
      alert("Failed to update status: " + errorMessage);
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleImageUpload = async (partId, file) => {
    try {
      setUploading(partId);
      const formData = new FormData();
      formData.append("issue_image", file);

      await updateImpactedPart(partId, formData);
      if (onUpdate) onUpdate();
    } catch (err) {
      // API error format: { errors: { field: ["message"] } } with status :unprocessable_entity
      let errorMessage = "Failed to upload image";
      if (err) {
        if (err.response && err.response.data && err.response.data.errors) {
          const errors = err.response.data.errors;
          const firstErrorField = Object.keys(errors)[0];
          if (firstErrorField && Array.isArray(errors[firstErrorField]) && errors[firstErrorField].length > 0) {
            errorMessage = errors[firstErrorField][0];
          } else if (firstErrorField) {
            errorMessage = String(errors[firstErrorField]);
          }
        } else if (err.message) {
          errorMessage = err.message;
        }
      }
      alert("Failed to upload image: " + errorMessage);
      console.error("Image upload error:", err);
    } finally {
      setUploading(null);
    }
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      open: "danger",
      analysed: "warning",
      received: "info",
      production: "primary",
      delivered: "success",
      invalid: "secondary",
    };
    return colors[status] || "secondary";
  };

  if (!parts || parts.length === 0) {
    return (
      <div className="alert alert-info">
        No impacted parts added yet. Click "Add Parts" to get started.
      </div>
    );
  }

  return (
    <>
      <Table bordered hover responsive size="sm">
        <thead className="table-light">
          <tr>
            <th style={{ width: "12%" }}>Label</th>
            <th style={{ width: "8%" }}>Image</th>
            <th style={{ width: "18%" }}>Issue</th>
            <th style={{ width: "12%" }}>Category</th>
            <th style={{ width: "18%" }}>Analysis</th>
            <th style={{ width: "15%" }}>Status</th>
            {!readOnly && <th style={{ width: "7%" }}>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {parts.map((part) => {
            const isEditing = editingPart === part.id;
            const isUploading = uploading === part.id;

            return (
              <tr key={part.id}>
                {/* Label */}
                <td>
                  <div className="d-flex align-items-center gap-2">
                    <strong>#{part.label}</strong>
                    {part.is_additional === true && (
                      <Badge color="warning" pill style={{ fontSize: "0.65rem" }}>
                        Additional
                      </Badge>
                    )}
                  </div>
                  {part.name && (
                    <div>
                      <small className="text-muted">{part.name}</small>
                    </div>
                  )}
                </td>

                {/* Image */}
                <td className="text-center">
                  {part.issue_image_url ? (
                    <div>
                      <img
                        src={part.issue_image_url}
                        alt="Issue"
                        style={{ width: "50px", height: "50px", objectFit: "cover", cursor: "pointer" }}
                        onClick={() => setImageModal(part.issue_image_url)}
                        className="rounded"
                      />
                    </div>
                  ) : (
                    <div>
                      {!readOnly && (
                        <>
                          <label
                            htmlFor={`image-upload-${part.id}`}
                            className="btn btn-sm btn-outline-secondary"
                            style={{ cursor: isUploading ? "wait" : "pointer" }}
                          >
                            {isUploading ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                              <i className="fas fa-upload"></i>
                            )}
                          </label>
                          <input
                            id={`image-upload-${part.id}`}
                            type="file"
                            accept="image/*"
                            style={{ display: "none" }}
                            onChange={(e) => {
                              if (e.target.files[0]) {
                                handleImageUpload(part.id, e.target.files[0]);
                              }
                            }}
                            disabled={isUploading}
                          />
                        </>
                      )}
                    </div>
                  )}
                </td>

                {/* Issue Description */}
                <td>
                  {isEditing ? (
                    <Input
                      type="textarea"
                      rows={2}
                      value={editData.issue_description || ""}
                      onChange={(e) =>
                        setEditData({ ...editData, issue_description: e.target.value })
                      }
                      bsSize="sm"
                    />
                  ) : (
                    <div
                      onClick={() => !readOnly && handleEditStart(part)}
                      style={{ cursor: readOnly ? "default" : "pointer" }}
                    >
                      {part.issue_description}
                    </div>
                  )}
                </td>

                {/* Categories */}
                <td>
                  {isEditing ? (
                    <Input
                      type="select"
                      multiple
                      value={editData.categories || []}
                      onChange={(e) => {
                        const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                        setEditData({ ...editData, categories: selectedOptions });
                      }}
                      bsSize="sm"
                      style={{ minHeight: "60px" }}
                    >
                      {constants && Array.isArray(constants.issue_categories) && constants.issue_categories.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat.charAt(0).toUpperCase() + cat.slice(1)}
                        </option>
                      ))}
                    </Input>
                  ) : (
                    <div className="d-flex flex-wrap gap-1">
                      {Array.isArray(part.categories) && part.categories.length > 0 ? (
                        part.categories.map((cat) => (
                          <span key={cat} className="badge bg-secondary" style={{ fontSize: "0.7rem" }}>
                            {cat}
                          </span>
                        ))
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </div>
                  )}
                </td>

                {/* Analysis */}
                <td>
                  {isEditing ? (
                    <Input
                      type="textarea"
                      rows={2}
                      value={editData.analysis || ""}
                      onChange={(e) => setEditData({ ...editData, analysis: e.target.value })}
                      bsSize="sm"
                    />
                  ) : (
                    <div
                      onClick={() => !readOnly && handleEditStart(part)}
                      style={{ cursor: readOnly ? "default" : "pointer" }}
                    >
                      {part.analysis || "-"}
                    </div>
                  )}
                </td>

                {/* Status */}
                <td>
                  <div className="d-flex flex-column gap-2">
                    <div>
                      <span className={`badge bg-${getStatusBadgeColor(part.status)}`}>
                        {part.status && typeof part.status === 'string'
                          ? part.status.charAt(0).toUpperCase() + part.status.slice(1)
                          : part.status}
                      </span>
                    </div>
                    {!readOnly && (
                      <div className="d-flex flex-wrap gap-1">
                        {updatingStatus === part.id ? (
                          <span className="text-muted small">
                            <i className="fas fa-spinner fa-spin me-1"></i>Updating...
                          </span>
                        ) : (
                          (() => {
                            const currentStatus = part.status;
                            if (!constants || !constants.valid_transitions || !constants.valid_transitions.part) {
                              return null;
                            }
                            const validTransitions = constants.valid_transitions.part[currentStatus];
                            if (!Array.isArray(validTransitions) || validTransitions.length === 0) {
                              return null;
                            }

                            return validTransitions.map((nextStatus) => {
                              const getButtonColor = (status) => {
                                const colors = {
                                  analysed: "warning",
                                  received: "info",
                                  production: "primary",
                                  delivered: "success",
                                  invalid: "danger",
                                };
                                return colors[status] || "secondary";
                              };

                              return (
                                <Button
                                  key={nextStatus}
                                  color={getButtonColor(nextStatus)}
                                  size="sm"
                                  onClick={() => handleStatusChangeWithConfirmation(part.id, nextStatus, currentStatus)}
                                  style={{ fontSize: "0.75rem", padding: "2px 8px" }}
                                >
                                  Mark {nextStatus && typeof nextStatus === 'string'
                                    ? nextStatus.charAt(0).toUpperCase() + nextStatus.slice(1)
                                    : nextStatus}
                                </Button>
                              );
                            });
                          })()
                        )}
                      </div>
                    )}
                  </div>
                </td>

                {/* Actions */}
                {!readOnly && (
                  <td>
                    {isEditing ? (
                      <div className="d-flex gap-1">
                        <Button
                          color="success"
                          size="sm"
                          onClick={() => handleEditSave(part.id)}
                        >
                          <i className="fas fa-check"></i>
                        </Button>
                        <Button color="secondary" size="sm" onClick={handleEditCancel}>
                          <i className="fas fa-times"></i>
                        </Button>
                      </div>
                    ) : (
                      <div className="d-flex gap-1">
                        <Button
                          color="primary"
                          size="sm"
                          onClick={() => handleEditStart(part)}
                          id={`edit-part-${part.id}`}
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        <UncontrolledTooltip target={`edit-part-${part.id}`}>
                          Edit
                        </UncontrolledTooltip>

                        <Button
                          color="danger"
                          size="sm"
                          onClick={() => onDelete(part.id)}
                          id={`delete-part-${part.id}`}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                        <UncontrolledTooltip target={`delete-part-${part.id}`}>
                          Remove
                        </UncontrolledTooltip>
                      </div>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </Table>

      {/* Image Modal */}
      <Modal isOpen={!!imageModal} toggle={() => setImageModal(null)} size="lg">
        <ModalHeader toggle={() => setImageModal(null)}>Issue Image</ModalHeader>
        <ModalBody className="text-center">
          {imageModal && (
            <img src={imageModal} alt="Issue" style={{ maxWidth: "100%", maxHeight: "80vh" }} />
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default ImpactedPartsTable;

